/**
 * The classroom rules — imported by BOTH the Worker and the web app.
 *
 * Grading, XP, levels, badges and the "three I-don't-knows" reteach trigger
 * live here and only here (CLAUDE.md rule 14). The client uses the same
 * functions to animate an answer instantly; the server uses them to decide
 * what is recorded. If the two ever disagree, the server's copy is the one
 * that counts, and there is only one copy.
 */

export type Lang = 'es' | 'en'
export interface Text { es: string; en: string }

export type Step =
  | { id: string; type: 'slide'; emoji?: string; title: Text; body: Text; teacherNote?: Text }
  | { id: string; type: 'question'; kind: 'yesno'; prompt: Text; answer: boolean; explain: Text; teacherNote?: Text }
  | { id: string; type: 'question'; kind: 'choice'; prompt: Text; choices: Text[]; answer: number; explain: Text; teacherNote?: Text }
  | { id: string; type: 'question'; kind: 'guess'; prompt: Text; unit?: string; answer: number; tolerance?: number; explain: Text; teacherNote?: Text }
  | { id: string; type: 'poll'; prompt: Text; choices: Text[]; teacherNote?: Text }
  | { id: string; type: 'discuss'; prompt: Text; teacherNote?: Text }
  | { id: string; type: 'activity'; title: Text; body: Text; teacherNote?: Text }

export interface Lesson {
  id: string
  order: number
  minutes: number
  emoji?: string
  title: Text
  summary: Text
  objectives: Text[]
  steps: Step[]
  homework: Text
  milestone: MilestoneKey | null
}

export interface Module {
  module: { id: number; slug: string; emoji?: string; color: string; title: Text; summary: Text }
  lessons: Lesson[]
}

/** A step a student can answer. Slides, discussions and activities are taught, not answered. */
export function isAnswerable(step: Step): boolean {
  return step.type === 'question' || step.type === 'poll'
}

export type Result = 'right' | 'close' | 'wrong' | 'idk' | 'poll'

/** The value a student sends. `idk` is the "No sé" button, on every question. */
export type AnswerValue = { idk: true } | { value: boolean | number }

/**
 * Grade an answer. Never throws on a malformed value — a garbled answer is
 * graded wrong, because refusing it would cost the student their XP for trying.
 */
export function grade(step: Step, answer: AnswerValue): Result {
  if ('idk' in answer) return 'idk'
  if (step.type === 'poll') return 'poll'
  if (step.type !== 'question') return 'wrong'
  const v = answer.value
  switch (step.kind) {
    case 'yesno':
      return v === step.answer ? 'right' : 'wrong'
    case 'choice':
      return v === step.answer ? 'right' : 'wrong'
    case 'guess': {
      if (typeof v !== 'number' || !Number.isFinite(v)) return 'wrong'
      if (v === step.answer) return 'right'
      const tol = step.tolerance ?? 0.2
      const off = Math.abs(v - step.answer) / Math.max(Math.abs(step.answer), 1)
      if (off <= tol / 2) return 'right'
      return off <= tol ? 'close' : 'wrong'
    }
  }
}

/**
 * XP. Every tap earns something — trying is the behaviour being rewarded, and
 * an honest "No sé" is worth more than a guess made to avoid saying it,
 * because it is what lets a teacher help. A streak of right answers adds a
 * bonus that grows and then caps, so one lucky run cannot run away with the
 * leaderboard.
 */
export const XP = { right: 100, close: 70, wrong: 25, idk: 15, poll: 30 } as const

export function xpFor(result: Result, streakBefore: number): number {
  const base = XP[result]
  if (result !== 'right') return base
  return base + Math.min(streakBefore, 5) * 20
}

/** The right-answer streak after this result. `close` keeps it alive; a poll does not touch it. */
export function nextStreak(result: Result, streak: number): number {
  if (result === 'right') return streak + 1
  if (result === 'close' || result === 'poll') return streak
  return 0
}

/**
 * The reteach trigger: THREE "No sé" in a row, across whatever questions came
 * between. Polls are skipped (they have no right answer to not know); any real
 * attempt resets the count.
 */
export const IDK_RUN = 3

/** Answering the call: paid when the teacher taps "Listo" after calling on you. */
export const CALL_BONUS = 50

export function idkRun(results: Result[]): number {
  let n = 0
  for (let i = results.length - 1; i >= 0; i--) {
    const r = results[i]
    if (r === 'poll') continue
    if (r !== 'idk') break
    n++
  }
  return n
}

export function needsReteach(results: Result[]): boolean {
  return idkRun(results) >= IDK_RUN
}

/**
 * Levels — out of the nest. The course is a maturity process: an egg becomes
 * a chick (pichón), a fledgling (volantón), flies solo, and is an adult.
 */
export const LEVELS: { min: number; title: Text }[] = [
  { min: 0, title: { es: 'Huevo', en: 'Egg' } },
  { min: 500, title: { es: 'Pichón', en: 'Chick' } },
  { min: 1500, title: { es: 'Volantón', en: 'Fledgling' } },
  { min: 3500, title: { es: 'Vuela solo', en: 'Flying solo' } },
  { min: 7000, title: { es: 'Adulto', en: 'Adult' } },
  { min: 12000, title: { es: 'Adulto certificado', en: 'Certified adult' } },
]

export function levelFor(xp: number): { index: number; title: Text; floor: number; next: number | null; progress: number } {
  let index = 0
  for (let i = 0; i < LEVELS.length; i++) if (xp >= (LEVELS[i] as { min: number }).min) index = i
  const cur = LEVELS[index] as { min: number; title: Text }
  const nxt = LEVELS[index + 1]
  const progress = nxt ? (xp - cur.min) / (nxt.min - cur.min) : 1
  return { index, title: cur.title, floor: cur.min, next: nxt ? nxt.min : null, progress: Math.max(0, Math.min(1, progress)) }
}

/** Milestones — the real-world checklist from Quentin's tracker. Minors do these with a parent. */
export const MILESTONES = [
  { key: 'bank_account', title: { es: 'Cuenta de banco o cooperativa', en: 'Bank or credit union account' } },
  { key: 'bank_app', title: { es: 'La app de tu banco', en: "Your bank's app" } },
  { key: 'budget', title: { es: 'Tu primer presupuesto', en: 'Your first budget' } },
  { key: 'financial_goal', title: { es: 'Una meta de dinero', en: 'A money goal' } },
  { key: 'emergency_fund', title: { es: 'Fondo de emergencia empezado', en: 'Emergency fund started' } },
  { key: 'investment_account', title: { es: 'Cuenta de inversión', en: 'Investment account' } },
  { key: 'credit_report', title: { es: 'Viste tu informe de crédito', en: 'Checked your credit report' } },
] as const satisfies readonly { key: string; title: Text }[]

export type MilestoneKey = (typeof MILESTONES)[number]['key']

export function isMilestone(key: unknown): key is MilestoneKey {
  return typeof key === 'string' && MILESTONES.some((m) => m.key === key)
}

export const MILESTONE_XP = 250

/**
 * XP a teacher gives by hand: a whole number, never zero, at most GRANT_MAX
 * either way. Negative takes points back (a correction). Checked on the server;
 * the form imports the same rule.
 */
export const GRANT_MAX = 10000

export function validGrant(xp: unknown): xp is number {
  return typeof xp === 'number' && Number.isInteger(xp) && xp !== 0 && Math.abs(xp) <= GRANT_MAX
}

/** Badges, derived from counts — never stored, so a new badge applies to history too. */
export interface Stats {
  answers: number
  right: number
  idk: number
  bestStreak: number
  calledOn: number
  practiced: number
  milestones: number
  lessonsAttended: number
}

/**
 * Badges have three tiers — bronze, silver, gold — with thresholds that
 * stretch across the whole course, so they are not all gone in a week. Each
 * tier pays XP the moment it is reached (derived, like everything else here),
 * and every badge at gold unlocks the Corona de Campeón, which cannot be bought.
 */
export const TIER_XP = [0, 50, 150, 400] as const
export const TIER_NAMES: Text[] = [
  { es: '', en: '' },
  { es: 'Bronce', en: 'Bronze' },
  { es: 'Plata', en: 'Silver' },
  { es: 'Oro', en: 'Gold' },
]

export interface Badge { key: string; stat: keyof Stats; tiers: [number, number, number]; title: Text; desc: Text }

/** `desc` uses {n} for the next target. */
export const BADGES: Badge[] = [
  { key: 'answers', stat: 'answers', tiers: [10, 75, 250], title: { es: 'Participante', en: 'Participant' }, desc: { es: 'Contesta {n} preguntas en clase.', en: 'Answer {n} questions in class.' } },
  { key: 'right', stat: 'right', tiers: [25, 100, 300], title: { es: 'Cerebrito', en: 'Big brain' }, desc: { es: 'Acierta {n} preguntas en clase.', en: 'Get {n} questions right in class.' } },
  { key: 'streak', stat: 'bestStreak', tiers: [5, 10, 20], title: { es: 'En fuego', en: 'On fire' }, desc: { es: 'Acierta {n} seguidas en clase.', en: 'Get {n} right in a row in class.' } },
  { key: 'honest', stat: 'idk', tiers: [3, 10, 25], title: { es: 'Honesto', en: 'Honest' }, desc: { es: 'Di "no sé" con honestidad {n} veces. Los adultos de verdad lo hacen.', en: 'Honestly say "I don\'t know" {n} times. Real adults do.' } },
  { key: 'speaker', stat: 'calledOn', tiers: [2, 6, 15], title: { es: 'Explicador', en: 'Explainer' }, desc: { es: 'Explica frente a la clase {n} veces.', en: 'Explain in front of the class {n} times.' } },
  { key: 'practice', stat: 'practiced', tiers: [10, 50, 150], title: { es: 'Estudioso', en: 'Studious' }, desc: { es: 'Practica {n} preguntas por tu cuenta.', en: 'Practice {n} questions on your own.' } },
  { key: 'milestone', stat: 'milestones', tiers: [2, 4, 7], title: { es: 'Del dicho al hecho', en: 'Did it for real' }, desc: { es: 'Logra {n} cosas de verdad, verificadas por tu maestro.', en: 'Do {n} real-world things, verified by your teacher.' } },
  { key: 'regular', stat: 'lessonsAttended', tiers: [3, 10, 25], title: { es: 'Presente', en: 'Showed up' }, desc: { es: 'Está en {n} lecciones en vivo.', en: 'Be in {n} live lessons.' } },
]

export function badgeTier(b: Badge, s: Stats): number {
  const v = s[b.stat]
  return b.tiers.filter((t) => v >= t).length
}

export function badgeXp(s: Stats): number {
  let xp = 0
  for (const b of BADGES) for (let t = 1; t <= badgeTier(b, s); t++) xp += TIER_XP[t] as number
  return xp
}

export function allGold(s: Stats): boolean {
  return BADGES.every((b) => badgeTier(b, s) === 3)
}

/**
 * The line a student sees after tapping. Chosen at random from a pool per
 * result, and NEVER negative: a wrong answer is "almost", an I-don't-know is
 * praised for honesty. Puerto Rican first.
 */
export const CHEERS: Record<Result, Text[]> = {
  right: [
    { es: '¡Wepa!', en: 'Yes!' },
    { es: '¡Tremendo!', en: 'Nailed it!' },
    { es: '¡Brutal!', en: 'Awesome!' },
    { es: '¡Eso es!', en: "That's it!" },
    { es: '¡Nítido!', en: 'Clean!' },
    { es: '¡Durísimo!', en: 'So good!' },
    { es: '¡Ahí está!', en: 'There it is!' },
  ],
  close: [
    { es: '¡Cerquita!', en: 'So close!' },
    { es: '¡Casi casi!', en: 'Almost exactly!' },
    { es: '¡Buen ojo!', en: 'Good eye!' },
  ],
  wrong: [
    { es: '¡Casi! Ahora ya lo sabes.', en: 'Almost! Now you know.' },
    { es: 'Buen intento — así se aprende.', en: "Good try — that's how you learn." },
    { es: 'Esa confunde a muchos adultos.', en: 'That one trips up plenty of adults.' },
    { es: '¡Arriba! La próxima es tuya.', en: 'Keep going! The next one is yours.' },
  ],
  idk: [
    { es: 'Decir "no sé" es de adultos.', en: 'Saying "I don\'t know" is very grown-up.' },
    { es: '¡Honestidad! Para eso estamos.', en: "Honesty! That's what we're here for." },
    { es: 'Tranqui — ahora lo aprendes.', en: "No stress — you're about to learn it." },
  ],
  poll: [
    { es: '¡Ganaste XP!', en: 'You earned XP!' },
    { es: '¡Anotado — y cobraste!', en: 'Counted — and you got paid!' },
    { es: '¡Pa\' la alcancía!', en: 'Into the piggy bank!' },
  ],
}

/** The fun burst on EVERY tap, before the result is known — the endorphin kick. */
export const TAPS: Text[] = [
  { es: '¡Pa\' dentro!', en: 'Locked in!' },
  { es: '¡Enviado!', en: 'Sent!' },
  { es: '¡Boom!', en: 'Boom!' },
  { es: '¡Zas!', en: 'Zap!' },
  { es: '¡Listo!', en: 'Done!' },
]

export function pick<T>(list: readonly T[], rand: () => number = Math.random): T {
  return list[Math.floor(rand() * list.length) % list.length] as T
}

/**
 * Class join codes: ONE letter and TWO numbers — "K47". Short enough to read
 * off a projector and type on a phone in a second; 2,300 of them is plenty
 * for the live classes at any one time (the insert retries on a collision).
 * No look-alike letters (I, L, O). Older six-character codes still join.
 */
const CODE_LETTERS = 'ABCDEFGHJKMNPQRSTUVWXYZ'

export function makeCode(rand: () => number = Math.random): string {
  const letter = CODE_LETTERS[Math.floor(rand() * CODE_LETTERS.length)] as string
  const n = Math.floor(rand() * 100)
  return `${letter}${String(n).padStart(2, '0')}`
}

export function normalizeCode(input: string): string {
  return input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 6)
}

export function validCode(code: string): boolean {
  return /^[A-Z][0-9]{2}$/.test(code) || /^[A-Z0-9]{6}$/.test(code)
}

/**
 * The call-on pick. Uniform over the pool, except that anybody called on in
 * the last `recent` picks is only chosen when nobody else is left — so the
 * same kid isn't called three times while others hide.
 */
export function pickCallOn(pool: string[], recent: string[], rand: () => number = Math.random): string | null {
  if (!pool.length) return null
  const fresh = pool.filter((id) => !recent.includes(id))
  return pick(fresh.length ? fresh : pool, rand)
}

export type Pool = 'right' | 'wrong' | 'idk' | 'none' | 'any'

/** Whether a question has a right answer at all — polls do not, so there is no right/wrong pool for them. */
export function correctText(step: Step, lang: Lang): string | null {
  if (step.type !== 'question') return null
  if (step.kind === 'yesno') return step.answer ? (lang === 'es' ? 'Sí' : 'Yes') : 'No'
  if (step.kind === 'choice') return step.choices[step.answer]?.[lang] ?? null
  return `${step.unit === '$' ? '$' : ''}${step.answer.toLocaleString(lang === 'es' ? 'es-PR' : 'en-US')}${step.unit && step.unit !== '$' ? ` ${step.unit}` : ''}`
}

/**
 * What the called-on student's phone says under "¡Te toca!". It depends on
 * WHY they were picked: somebody who got it right explains it; somebody who
 * got it wrong walks the class through their thinking (the most useful thing
 * in the room — someone else thought it too); a "No sé" is invited to say
 * what they DO know; somebody who didn't answer gets a fresh chance. Several
 * lines per case, picked at random, so it never reads like a script.
 */
export type CallSituation = 'right' | 'wrong' | 'idk' | 'none' | 'poll' | 'talk'

export function callSituation(kind: string, result: Result | null): CallSituation {
  if (kind === 'poll') return result ? 'poll' : 'none'
  if (kind !== 'question') return 'talk'
  if (!result) return 'none'
  if (result === 'right' || result === 'close') return 'right'
  if (result === 'idk') return 'idk'
  return 'wrong'
}

export const CALL_LINES: Record<CallSituation, Text[]> = {
  right: [
    { es: 'Le diste. Explícale a la clase cómo llegaste ahí.', en: 'You got it. Tell the class how you got there.' },
    { es: '¡Profe por un minuto! ¿Por qué esa es la respuesta?', en: "Teacher for a minute! Why is that the answer?" },
    { es: 'Dilo con tus palabras, como se lo explicarías a un pana.', en: "Say it in your own words, like you'd explain it to a friend." },
  ],
  wrong: [
    { es: 'Cuéntanos cómo lo pensaste. Seguro alguien más pensó igual.', en: 'Walk us through your thinking. Someone else surely thought the same.' },
    { es: '¿Qué te hizo escoger esa? Así es que se aprende de verdad.', en: "What made you pick that one? That's how real learning happens." },
    { es: 'Equivocarse en voz alta es de valientes. ¿Qué pensaste?', en: "Being wrong out loud takes guts. What were you thinking?" },
  ],
  idk: [
    { es: 'No pasa nada. Dinos qué parte sí entiendes.', en: "No stress. Tell us the part you DO get." },
    { es: 'Pregúntale algo a la clase — lo que te tenga confundido.', en: "Ask the class something — whatever's confusing you." },
    { es: '¿Qué necesitarías saber para contestarla?', en: 'What would you need to know to answer it?' },
  ],
  none: [
    { es: 'Te toca escoger ahora: ¿cuál dirías tú y por qué?', en: "Your pick now: which would you choose, and why?" },
    { es: 'Sin presión: ¿qué te dice tu instinto?', en: 'No pressure: what does your gut say?' },
  ],
  poll: [
    { es: 'Cuéntanos por qué escogiste eso.', en: 'Tell us why you picked that.' },
    { es: '¿Qué te pasó por la mente cuando votaste?', en: 'What went through your mind when you voted?' },
  ],
  talk: [
    { es: 'Comparte lo que piensas con la clase.', en: 'Share what you think with the class.' },
    { es: '¿Qué opinas tú? Dilo en voz alta.', en: 'What do you think? Say it out loud.' },
    { es: '¿Te ha pasado algo así? Cuéntanos.', en: 'Has something like this happened to you? Tell us.' },
  ],
}
